/*
 * Cairo time from the tz database. Egypt is UTC+3 in summer and UTC+2 in winter (summer time from the last Friday of
 * April to the last Thursday of October), so a fixed «+ 3 hours» or «+03:00» is wrong half the year: the first switch
 * this system lived through was 2026-10-30 00:00 (found in the full review, 2026-10-29). Everything that turns an
 * instant into a Cairo day / clock, or a Cairo wall time into an instant, goes through here.
 */
const HOUR = 3_600_000
const FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })

/** Cairo's offset from UTC at that instant, in ms (3 h in summer, 2 h in winter). */
export function cairoOffsetMs(ms: number = Date.now()): number {
  const p = Object.fromEntries(FMT.formatToParts(new Date(ms)).map((x) => [x.type, x.value]))
  const wall = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second))
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / (15 * 60_000)) * 15 * 60_000
}

/** The instant shifted to Cairo wall clock, read with getUTC* / toISOString (what «ms + 3 h» meant). */
export const cairoWallMs = (ms: number = Date.now()) => ms + cairoOffsetMs(ms)
/** «YYYY-MM-DD» in Cairo. */
export const cairoDayOf = (ms: number = Date.now()) => new Date(cairoWallMs(ms)).toISOString().slice(0, 10)
/** «YYYY-MM-DDTHH:mm:ss» in Cairo (zone-less, as Dispatch writes it). */
export const cairoStampOf = (ms: number = Date.now()) => new Date(cairoWallMs(ms)).toISOString().slice(0, 19)
/** Hour 0-23 in Cairo. */
export const cairoHourOf = (ms: number = Date.now()) => new Date(cairoWallMs(ms)).getUTCHours()

/**
 * A Cairo wall time («YYYY-MM-DD», «YYYY-MM-DDTHH:mm», «…:ss», «… HH:mm:ss.fff», no zone) → the instant. In the hour
 * the clocks go back (23:00-24:00 twice) the first, summer one is taken; a time skipped in spring maps an hour on
 * (2026-04-24 00:00, which never happened, is 01:00 summer time: the 23-hour day 2026-04-23 ends there, not an hour
 * early as the old two-step guess had it).
 */
export function cairoWallToMs(wall: string): number {
  const s = wall.trim().replace(' ', 'T')
  const t = Date.parse(/T/.test(s) ? `${s}Z` : `${s}T00:00:00Z`)
  if (!Number.isFinite(t)) return NaN
  // the offset a day before and a day after: equal on every day but the two the clocks change
  const before = cairoOffsetMs(t - 30 * HOUR), after = cairoOffsetMs(t + 30 * HOUR)
  const shown = (o: number) => cairoWallMs(t - o) === t
  const first = Math.max(before, after), second = Math.min(before, after)
  if (shown(first)) return t - first // the earlier instant: the summer one in the repeated hour
  if (shown(second)) return t - second
  return t - before // skipped in spring: read with the offset before the gap, an hour on
}
/** The start of a Cairo day («YYYY-MM-DD») as an instant. */
export const cairoDayStartMs = (day: string) => cairoWallToMs(`${day.slice(0, 10)}T00:00:00`)
/** «+03:00» / «+02:00» for that instant (for an ISO string Dispatch or a partner reads with a zone). */
export function cairoZoneSuffix(ms: number = Date.now()): string {
  const h = cairoOffsetMs(ms) / HOUR
  return `+${String(Math.trunc(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`
}
/** The start of the Cairo day after `day`: a day's end, right on the 25- and 23-hour days the clocks change. */
export function cairoNextDayStartMs(day: string): number {
  const d = new Date(`${day.slice(0, 10)}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 1)
  return cairoDayStartMs(d.toISOString().slice(0, 10))
}
